"use client";

import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Users, Copy, Check, Share2 } from 'lucide-react';
import { useAuthContext } from '@/components/providers/AuthProvider';
import { usePostInteractions } from '@/hooks/usePostInteractions';
import { useToast } from '@/hooks/useToast';

interface ShareModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  postId: string;
  isProfilePost?: boolean;
  onShareComplete?: () => void;
}

export default function ShareModal({
  open,
  onOpenChange,
  postId,
  isProfilePost = false,
  onShareComplete
}: ShareModalProps) {
  const { user } = useAuthContext();
  const { sharePost, copyPostLink, isLoading } = usePostInteractions();
  const { toast } = useToast();
  const [linkCopied, setLinkCopied] = useState(false);

  const handleShareToProfile = async () => {
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please sign in to share posts",
        variant: "destructive",
      });
      return;
    }

    try {
      await sharePost(postId, isProfilePost);
      toast({
        title: "Post shared",
        description: "This post has been shared to your profile",
        variant: "success",
      });
      onShareComplete?.();
      onOpenChange(false);
    } catch (error) {
      console.error('Error sharing post:', error);
      toast({
        title: "Error",
        description: "Failed to share post. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleCopyLink = async () => {
    try {
      await copyPostLink(postId, isProfilePost);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (error) {
      console.error('Error copying link:', error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-800 border-slate-700 text-white sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Share2 className="h-5 w-5 text-blue-500" />
            Share Post
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Choose how you'd like to share this post
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-4">
          {/* Share to Profile */}
          <Button
            onClick={handleShareToProfile}
            disabled={isLoading || !user}
            className="w-full justify-start bg-blue-600 hover:bg-blue-700 text-white h-auto py-3"
          >
            <Users className="h-5 w-5 mr-3" />
            <div className="flex-1 text-left">
              <div className="font-semibold">Share to Your Profile</div>
              <div className="text-sm opacity-90">Repost this to your profile feed</div>
            </div>
          </Button>

          {/* Copy Link */}
          <Button
            onClick={handleCopyLink}
            variant="outline"
            className="w-full justify-start border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white h-auto py-3"
          >
            {linkCopied ? (
              <Check className="h-5 w-5 mr-3 text-green-500" />
            ) : (
              <Copy className="h-5 w-5 mr-3" />
            )}
            <div className="flex-1 text-left">
              <div className="font-semibold">
                {linkCopied ? 'Link Copied!' : 'Copy Link'}
              </div>
              <div className="text-sm opacity-70">
                {linkCopied ? 'Link copied to clipboard' : 'Copy post link to share elsewhere'}
              </div>
            </div>
          </Button>
        </div>

        {!user && (
          <div className="mt-4 p-3 bg-slate-700/50 rounded-lg text-sm text-slate-300">
            Sign in to share posts to your profile
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

